/**
 * Position Management — Utility Functions (Redesigned)
 */

/**
 * Format a number as currency with proper currency code
 */
export const formatCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  } catch {
    return `${formatNumber(amount, 2)} ${currency}`;
  }
};

/**
 * Format a number with specified decimals
 */
export const formatNumber = (number, decimals = 2) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number || 0);
};

/**
 * Format a date/time for display
 */
export const formatDateTime = (date) => {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(date));
};

/**
 * Format a full date
 */
export const formatDate = (date) => {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

/**
 * Get a human-readable side label
 */
export const getSideLabel = (side) => {
  if (side === 'buy') return 'LONG';
  if (side === 'sell') return 'SHORT';
  return side?.toUpperCase() || '---';
};

/**
 * Get CSS class for a side badge
 */
export const getSideColorClass = (side) => {
  if (side === 'buy') return 'long';
  if (side === 'sell') return 'short';
  return 'flat';
};

/**
 * Get the net side string from net quantity
 */
export const getNetSide = (netQty) => {
  if (netQty > 0) return 'long';
  if (netQty < 0) return 'short';
  return 'flat';
};

/**
 * Extract positions data from API response
 */
export const extractPositionsData = (apiResponse) => {
  if (!apiResponse) return {};

  if (apiResponse.success && apiResponse.data) {
    if (apiResponse.data.status === 'failed') {
      throw new Error(apiResponse.data.error || apiResponse.data.info || 'Unknown error');
    }
    return apiResponse.data.data || apiResponse.data;
  }
  if (apiResponse.data?.data) return apiResponse.data.data;
  if (apiResponse.data) return apiResponse.data;
  return apiResponse;
};

/**
 * Extract summary data from API response
 */
export const extractSummaryData = (apiResponse) => {
  if (!apiResponse) return null;

  if (apiResponse.success && apiResponse.data) {
    if (apiResponse.data.status === 'failed') {
      throw new Error(apiResponse.data.error || apiResponse.data.info || 'Unknown error');
    }
    return apiResponse.data.data || apiResponse.data;
  }
  if (apiResponse.data?.data) return apiResponse.data.data;
  if (apiResponse.data) return apiResponse.data;
  return apiResponse;
};

/**
 * Extract user positions data from API response
 */
export const extractUserPositionsData = (apiResponse) => {
  if (!apiResponse) return [];

  if (apiResponse.success && apiResponse.data) {
    if (apiResponse.data.status === 'failed') {
      throw new Error(apiResponse.data.error || apiResponse.data.info || 'Unknown error');
    }
    const data = apiResponse.data.data;
    if (Array.isArray(data)) return data;
    if (data?.positions) return data.positions;
    return [];
  }
  if (apiResponse.data?.data) {
    const data = apiResponse.data.data;
    if (Array.isArray(data)) return data;
    if (data?.positions) return data.positions;
    return [];
  }
  if (Array.isArray(apiResponse.data)) return apiResponse.data;
  return [];
};

/**
 * Extract user orders data from API response
 */
export const extractUserOrdersData = (apiResponse) => {
  if (!apiResponse) return [];

  if (apiResponse.success && apiResponse.data) {
    if (apiResponse.data.status === 'failed') {
      throw new Error(apiResponse.data.error || apiResponse.data.info || 'Unknown error');
    }
    const data = apiResponse.data.data;
    if (Array.isArray(data)) return data;
    if (data?.orders) return data.orders;
    return [];
  }
  if (apiResponse.data?.data) {
    const data = apiResponse.data.data;
    if (Array.isArray(data)) return data;
    if (data?.orders) return data.orders;
    return [];
  }
  if (Array.isArray(apiResponse.data)) return apiResponse.data;
  return [];
};

/**
 * Get order status color/tag class
 */
export const getOrderStatusClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'warning';
    case 'inprocess': return 'info';
    case 'cancelling': return 'warning';
    case 'cancelled': return 'danger';
    case 'filled': return 'success';
    case 'partiallyfilled': return 'success';
    case 'completed': return 'success';
    case 'rejected': return 'danger';
    default: return '';
  }
};

/**
 * Human-readable order status label
 */
export const getOrderStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'Pending';
    case 'inprocess': return 'In Process';
    case 'cancelling': return 'Cancelling';
    case 'cancelled': return 'Cancelled';
    case 'filled': return 'Filled';
    case 'partiallyfilled': return 'Partially Filled';
    case 'completed': return 'Completed';
    case 'rejected': return 'Rejected';
    default: return status || '---';
  }
};

/**
 * Check if an order is removable
 */
export const isOrderRemovable = (status) => {
  const s = status?.toLowerCase();
  return s === 'pending' || s === 'inprocess' || s === 'cancelling';
};
